import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Search } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

// The tenant fixes what the sender packed wrongly while a correction request is open (see work_routes.correctable_work).
export default function ItemCorrectionPanel({ workId, items, onChanged, onMessage }) {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const base = `${API}/pallet-work/${workId}/items`;

  useEffect(() => {
    if (!query || picked) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/products`, { ...ax, params: { q: query } });
        setResults(data.slice(0, 8));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, picked]);

  const run = async (request, successKey) => {
    try {
      await request();
      if (successKey) onMessage("success", t(successKey));
      await onChanged();
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      onMessage("error", typeof detail === "string" ? detail : t("planning.error"));
      return false;
    }
  };

  const add = async () => {
    const ok = await run(() => axios.post(base, { asin: picked.asin, quantity }, ax), "planning.item_added");
    if (ok) { setPicked(null); setQuery(""); setQuantity(1); }
  };

  return (
    <details open className="bg-white rounded-lg border border-amber-200" data-testid="item-correction-panel">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-amber-800 bg-amber-50 rounded-lg">
        {t("planning.items_title")}
      </summary>
      <div className="p-4 space-y-4">
        <p className="text-sm text-slate-600">{t("planning.items_hint")}</p>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{item.product_name}</p>
                <p className="text-xs font-mono text-slate-500">{item.asin}</p>
              </div>
              <Button variant="outline" className="h-9 w-9 p-0 text-lg" disabled={item.quantity <= 1}
                onClick={() => run(() => axios.put(`${base}/${item.id}`, { quantity: item.quantity - 1 }, ax))}>−</Button>
              <span className="w-8 text-center font-bold text-sm">{item.quantity}</span>
              <Button variant="outline" className="h-9 w-9 p-0 text-lg"
                onClick={() => run(() => axios.put(`${base}/${item.id}`, { quantity: item.quantity + 1 }, ax))}>+</Button>
              <Button variant="ghost" className="h-9 w-9 p-0 text-red-500 hover:text-red-700" aria-label={t("planning.item_remove")}
                onClick={() => run(() => axios.delete(`${base}/${item.id}`, ax), "planning.item_removed")}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={query} onChange={event => { setQuery(event.target.value); setPicked(null); }}
              placeholder={t("planning.item_search")} className="pl-9 h-10" />
            {results.length > 0 && (
              <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {results.map(product => (
                  <button key={product.id} type="button" className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-0 border-slate-50"
                    onClick={() => { setPicked(product); setQuery(product.product_name); }}>
                    <p className="text-sm font-medium text-slate-900">{product.product_name}</p>
                    <p className="text-xs font-mono text-slate-500">{product.asin}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {picked && (
            <div className="flex items-center gap-2">
              <Button variant="outline" className="h-10 w-10 p-0 text-lg" onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</Button>
              <Input type="number" min="1" value={quantity} onFocus={event => event.target.select()}
                onChange={event => setQuantity(Number.parseInt(event.target.value, 10) || 1)} className="h-10 flex-1 text-center" />
              <Button variant="outline" className="h-10 w-10 p-0 text-lg" onClick={() => setQuantity(quantity + 1)}>+</Button>
              <Button onClick={add} className="h-10 gap-1 bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4" /> {t("planning.item_add")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </details>
  );
}
